import React, { useState, useEffect } from 'react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { useParams } from 'react-router-dom';
import { productService } from '../service/productservice';
import { AiFillHeart, AiOutlineHeart, AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { FaSpinner } from 'react-icons/fa'; // Spinner icon

const ProductDetail = () => {
  const { category, name } = useParams();
  const [product, setProduct] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [hasIncompatibleItems, setHasIncompatibleItems] = useState(false); // New state
  

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        const selectedProduct = await productService.getProductByCategoryAndName(category, name);
        setProduct(selectedProduct);

        const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
        // Check for incompatible order types
        const hasIncompatible = storedCartItems.some(item => item.orderType == "Large Box", "Small Box", "Discount");
        setHasIncompatibleItems(hasIncompatible);

        const storedFavorites = JSON.parse(localStorage.getItem('favoriteItems')) || [];
        setIsFavorite(storedFavorites.some((item) => item.id === selectedProduct.id));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [category, name]);

  const handleAddToCart = () => {
    if (!product || selectedQuantity === 0) return;

    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const existingItem = storedCartItems.find((item) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += selectedQuantity;
    } else {
      storedCartItems.push({ ...product, quantity: selectedQuantity });
    }

    localStorage.setItem('cartItems', JSON.stringify(storedCartItems));
    setCartCount(cartCount + selectedQuantity);
    setSelectedQuantity(0);
  };

  const toggleFavorite = () => {
    const storedFavorites = JSON.parse(localStorage.getItem('favoriteItems')) || [];
    if (isFavorite) {
      const updatedFavorites = storedFavorites.filter((item) => item.id !== product.id);
      localStorage.setItem('favoriteItems', JSON.stringify(updatedFavorites));
    } else {
      storedFavorites.push(product);
      localStorage.setItem('favoriteItems', JSON.stringify(storedFavorites));
    }
    setIsFavorite(!isFavorite);
  };

  const handleIncreaseQuantity = () => {
    if (selectedQuantity < product.quantity) {
      setSelectedQuantity((prev) => prev + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (selectedQuantity > 0) {
      setSelectedQuantity((prev) => prev - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FaSpinner className="text-4xl animate-spin text-green-500" />
      </div>
    );
  }

  if (!product) {
    return <div className="text-center mt-10 text-xl">Product not found</div>;
  }

  const isOutOfStock = product.quantity === 0;

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    // Check for incompatible order types
    const hasIncompatible = storedCartItems.some(item => item.orderType == "Large Box", "Small Box", "Discount");
    setHasIncompatibleItems(hasIncompatible);

    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
  };

  const forceUpdateProducts = () => {
    setForceUpdateKey(prevKey => prevKey + 1); // This will force a re-render in ProductCard
  };


  return (
    <div className="App">
      <Navbar cartCount={cartCount} updateCartCount={updateCartCount} forceUpdateProducts={forceUpdateProducts} />

     
      <div className="p-6 mt-28 max-sm:mt-20">
      {hasIncompatibleItems && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">
              Your cart contains items from a different box type. Please complete your current order or clear the cart before selecting items from the Large Box.
            </span>
            <button
              onClick={() => setHasIncompatibleItems(false)}
              className="absolute top-0 right-0 px-2 py-1"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex items-start max-sm:flex-col max-sm:items-center">
          {/* Product Image */}
          
          <div className="w-1/2 max-sm:w-full max-sm:mb-6">
            <div
              className={`h-[500px] max-sm:h-[300px] w-full rounded-lg bg-center bg-cover relative ${
                isOutOfStock ? 'grayscale' : ''
              }`}
              style={{ backgroundImage: `url(${product.img})` }}
            >
              {isOutOfStock && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                  <h2 className="text-white text-2xl font-semibold">Out of Stock</h2>
                </div>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="w-1/2 max-sm:w-full max-sm:px-4 ml-10 max-sm:ml-0 ">
            <h1 className="text-2xl max-sm:text-xl font-semibold">{product.name}</h1>
            <p className="text-xl text-green-600 mt-2">${product.price}</p>
            <p className="text-gray-600 mt-4 leading-relaxed">{product.description}</p>
            <p className="mt-6">
              <span className="font-bold">Category:</span> {product.category}
            </p>
            <p>
              <span className="font-bold">Brand:</span> {product.brand}
            </p>

            {/* Quantity and Buttons */}
            <div className="flex items-center mt-8 max-sm:flex-col max-sm:items-stretch">
              {!isOutOfStock && (
                <div className="flex items-center max-sm:justify-between">
                  <button
                    onClick={handleDecreaseQuantity}
                    className="p-2 bg-gray-200 rounded-full hover:bg-gray-300"
                  >
                    <AiOutlineMinus />
                  </button>
                  <span className="mx-4 text-lg">{selectedQuantity}</span>
                  <button
                    onClick={handleIncreaseQuantity}
                    className="p-2 bg-gray-200 rounded-full hover:bg-gray-300"
                  >
                    <AiOutlinePlus />
                  </button>
                </div>
              )}
              <button
                onClick={handleAddToCart}
                className={`max-sm:ml-0 max-sm:mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 ${
                  isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isOutOfStock || selectedQuantity === 0}
              >
                Add to Cart
              </button>
            </div>

          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetail;
